import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Search, CheckCircle, Circle, LogOut, Download, RefreshCw, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const [leads, setLeads] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [credentials, setCredentials] = useState(null);
  const [authError, setAuthError] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all', 'contacted', 'pending'
  const [leadToDelete, setLeadToDelete] = useState(null); // lead object for single delete dialog
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchLeads = useCallback(async () => {
    if (!credentials || !credentials.username || !credentials.password) {
      setLoading(false);
      return;
    }
    
    // Reset auth error on new attempt
    setAuthError(false);
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const auth = btoa(`${credentials.username}:${credentials.password}`);
      
      const response = await fetch(`${backendUrl}/api/admin/leads`, {
        headers: {
          'Authorization': `Basic ${auth}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setLeads(data);
        setLoading(false);
        setAuthError(false);
      } else if (response.status === 401) {
        // Mark as auth error but DON'T immediately log out
        // This prevents false logouts on temporary network issues
        setAuthError(true);
        setLoading(false);
        
        // Only show toast, don't clear credentials immediately
        toast.error('Authentication failed. Please refresh or re-login.');
      } else {
        // Other error - don't log out
        toast.error(`Server error: ${response.status}`);
        setLoading(false);
      }
    } catch (error) {
      // Network error - don't log out, just show error
      toast.error('Failed to connect to server');
      setLoading(false);
    }
  }, [credentials]);

  useEffect(() => {
    if (credentials) {
      setLoading(true);
      fetchLeads();
    }
  }, [credentials, fetchLeads]);

  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) {
      fetchLeads();
      return;
    }

    if (!credentials) return;

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const auth = btoa(`${credentials.username}:${credentials.password}`);
      
      const response = await fetch(`${backendUrl}/api/admin/leads/search?q=${encodeURIComponent(searchQuery)}`, {
        headers: {
          'Authorization': `Basic ${auth}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setLeads(data);
      }
    } catch (error) {
      toast.error('Search failed');
    }
  }, [searchQuery, credentials, fetchLeads]);

  const toggleContactedStatus = useCallback(async (leadId, currentStatus) => {
    if (!credentials) return;
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const auth = btoa(`${credentials.username}:${credentials.password}`);
      
      const response = await fetch(`${backendUrl}/api/admin/leads/${leadId}/contacted`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Basic ${auth}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchLeads();
      }
    } catch (error) {
      toast.error('Failed to update lead');
    }
  }, [credentials, fetchLeads]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await fetchLeads();
      toast.success('Leads refreshed successfully');
    } catch (error) {
      toast.error('Failed to refresh leads');
    } finally {
      setRefreshing(false);
    }
  }, [fetchLeads]);

  const handleDeleteLead = useCallback(async (leadId) => {
    if (!credentials) return;
    setDeleting(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const auth = btoa(`${credentials.username}:${credentials.password}`);

      const response = await fetch(`${backendUrl}/api/admin/leads/${leadId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Basic ${auth}` },
      });

      if (response.ok) {
        toast.success('Lead deleted');
        setLeadToDelete(null);
        // Optimistic update — remove locally, then refresh to stay in sync
        setLeads((prev) => prev.filter((l) => l.id !== leadId));
      } else if (response.status === 404) {
        toast.error('Lead not found');
        setLeadToDelete(null);
      } else {
        toast.error(`Failed to delete lead (${response.status})`);
      }
    } catch (error) {
      console.error('Delete lead failed:', error);
      toast.error('Failed to delete lead');
    } finally {
      setDeleting(false);
    }
  }, [credentials]);

  const handleDeleteAllLeads = useCallback(async () => {
    if (!credentials) return;
    setDeleting(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const auth = btoa(`${credentials.username}:${credentials.password}`);

      const response = await fetch(`${backendUrl}/api/admin/leads`, {
        method: 'DELETE',
        headers: { 'Authorization': `Basic ${auth}` },
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Deleted ${data.deleted_count || 0} lead(s)`);
        setLeads([]);
        setDeleteAllOpen(false);
      } else {
        toast.error(`Failed to delete leads (${response.status})`);
      }
    } catch (error) {
      console.error('Delete all leads failed:', error);
      toast.error('Failed to delete leads');
    } finally {
      setDeleting(false);
    }
  }, [credentials]);

  const handleLogin = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const username = formData.get('username');
    const password = formData.get('password');
    
    // Set credentials - useEffect will trigger fetchLeads
    setCredentials({ username, password });
  };

  const handleLogout = () => {
    setCredentials(null);
    setLeads([]);
  };

  // Memoize stats to prevent unnecessary recalculations
  const stats = useMemo(() => ({
    total: leads.length,
    contacted: leads.filter(l => l.contacted).length,
    pending: leads.filter(l => !l.contacted).length
  }), [leads]);

  // Filter leads based on selected filter
  const filteredLeads = useMemo(() => {
    if (filter === 'contacted') {
      return leads.filter(l => l.contacted);
    } else if (filter === 'pending') {
      return leads.filter(l => !l.contacted);
    }
    return leads; // 'all'
  }, [leads, filter]);

  const exportToCSV = () => {
    const csv = [
      ['Name', 'Mobile', 'Submitted At', 'Contacted'],
      ...filteredLeads.map(lead => [
        lead.name,
        lead.mobile,
        new Date(lead.submitted_at).toLocaleString(),
        lead.contacted ? 'Yes' : 'No'
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (!credentials) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-white flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md border border-orange-100">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold" style={{ color: '#f15b29' }}>Admin Login</h1>
            <p className="text-gray-600 mt-2">Wealth Calculator Dashboard</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Username</label>
              <Input
                name="username"
                type="text"
                placeholder="Enter username"
                required
                className="w-full"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Password</label>
              <Input
                name="password"
                type="password"
                placeholder="Enter password"
                required
                className="w-full"
              />
            </div>
            
            <Button
              type="submit"
              className="w-full h-11 text-base font-semibold"
              style={{ backgroundColor: '#f15b29' }}
            >
              Login
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold" style={{ color: '#f15b29' }}>Lead Dashboard</h1>
              <p className="text-sm text-gray-600">Wealth Calculator Leads</p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center gap-2"
                data-testid="admin-refresh-btn"
              >
                <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
                Refresh
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={exportToCSV}
                className="flex items-center gap-2"
                data-testid="admin-export-csv-btn"
              >
                <Download size={16} />
                Export CSV
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDeleteAllOpen(true)}
                disabled={stats.total === 0}
                className="flex items-center gap-2 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                data-testid="admin-delete-all-btn"
              >
                <Trash2 size={16} />
                Delete All
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="flex items-center gap-2"
                data-testid="admin-logout-btn"
              >
                <LogOut size={16} />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search Bar */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                type="text"
                placeholder="Search by name or mobile number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="pl-10"
                data-testid="admin-search-input"
              />
            </div>
            <Button onClick={handleSearch} style={{ backgroundColor: '#f15b29' }} data-testid="admin-search-btn">
              Search
            </Button>
            {searchQuery && (
              <Button
                variant="outline"
                onClick={() => {
                  setSearchQuery('');
                  fetchLeads();
                }}
                data-testid="admin-search-clear-btn"
              >
                Clear
              </Button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-testid="stat-total">
            <div className="text-sm text-gray-600">Total Leads</div>
            <div className="text-3xl font-bold mt-1" style={{ color: '#f15b29' }}>{stats.total}</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-testid="stat-contacted">
            <div className="text-sm text-gray-600">Contacted</div>
            <div className="text-3xl font-bold text-green-600 mt-1">{stats.contacted}</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-testid="stat-pending">
            <div className="text-sm text-gray-600">Pending</div>
            <div className="text-3xl font-bold text-amber-600 mt-1">{stats.pending}</div>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-4 flex flex-wrap gap-2" data-testid="admin-filter-controls">
          <Button
            variant={filter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('all')}
            style={filter === 'all' ? { backgroundColor: '#f15b29' } : undefined}
            data-testid="filter-all-btn"
          >
            All ({stats.total})
          </Button>
          <Button
            variant={filter === 'contacted' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('contacted')}
            className={filter === 'contacted' ? 'bg-green-600 hover:bg-green-700 text-white' : ''}
            data-testid="filter-contacted-btn"
          >
            <CheckCircle className="w-4 h-4 mr-1" />
            Contacted ({stats.contacted})
          </Button>
          <Button
            variant={filter === 'pending' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('pending')}
            className={filter === 'pending' ? 'bg-amber-600 hover:bg-amber-700 text-white' : ''}
            data-testid="filter-pending-btn"
          >
            <Circle className="w-4 h-4 mr-1" />
            Pending ({stats.pending})
          </Button>
        </div>

        {/* Leads Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-testid="admin-leads-table">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Name</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Mobile</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Corpus</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Monthly<br/>Withdrawal</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Legacy<br/>Balance</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Legacy<br/>Year</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Depletes<br/>In</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Submitted</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="10" className="px-6 py-8 text-center text-gray-500">
                      Loading leads...
                    </td>
                  </tr>
                ) : filteredLeads.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="px-6 py-8 text-center text-gray-500" data-testid="admin-no-leads">
                      No leads found
                    </td>
                  </tr>
                ) : (
                  filteredLeads.map((lead) => (
                    <tr key={lead.id} className="hover:bg-gray-50" data-testid={`lead-row-${lead.id}`}>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{lead.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono">{lead.mobile}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono text-right">
                        {lead.corpus ? `₹${(lead.corpus / 10000000).toFixed(2)}Cr` : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono text-right">
                        {lead.monthly_withdrawal ? `₹${(lead.monthly_withdrawal / 1000).toFixed(0)}k` : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono text-right">
                        {lead.legacy_balance ? `₹${(lead.legacy_balance / 10000000).toFixed(2)}Cr` : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono text-center">
                        {lead.legacy_year || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 font-mono text-center">
                        {lead.depletes_in ? `${lead.depletes_in}yr` : 'Never'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {new Date(lead.submitted_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-3">
                        {lead.contacted ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircle size={14} />
                            Contacted
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                            <Circle size={14} />
                            Pending
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => toggleContactedStatus(lead.id, lead.contacted)}
                            className={lead.contacted
                              ? "text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                              : "text-green-600 hover:text-green-700 hover:bg-green-50"
                            }
                            data-testid={`toggle-contacted-${lead.id}`}
                          >
                            {lead.contacted ? 'Mark Pending' : 'Mark Contacted'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setLeadToDelete(lead)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                            data-testid={`delete-lead-${lead.id}`}
                            aria-label="Delete lead"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Single lead delete confirmation */}
      <AlertDialog open={!!leadToDelete} onOpenChange={(open) => !open && setLeadToDelete(null)}>
        <AlertDialogContent data-testid="delete-lead-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this lead?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove{' '}
              <span className="font-semibold text-foreground">{leadToDelete?.name}</span>
              {' '}({leadToDelete?.mobile}) from the dashboard. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="delete-lead-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting}
              onClick={(e) => {
                e.preventDefault();
                if (leadToDelete) handleDeleteLead(leadToDelete.id);
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="delete-lead-confirm"
            >
              {deleting ? 'Deleting...' : 'Yes, delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete all leads confirmation */}
      <AlertDialog open={deleteAllOpen} onOpenChange={setDeleteAllOpen}>
        <AlertDialogContent data-testid="delete-all-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">Delete ALL leads?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove{' '}
              <span className="font-semibold text-foreground">all {stats.total} lead(s)</span>
              {' '}from the database. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="delete-all-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting}
              onClick={(e) => {
                e.preventDefault();
                handleDeleteAllLeads();
              }}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="delete-all-confirm"
            >
              {deleting ? 'Deleting...' : 'Yes, delete all'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminDashboard;
